import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  IconStepMatch,
  IconStepPrepare,
  IconStepProfile,
  IconStepSearch,
  IconThinkingBrain,
} from '@/components/email-finder/icons';
import { useTranslation } from 'next-i18next';

interface SearchAnimateProps {
  /** 提示词验证状态: loading | success | error (用户输入的提示词是否有效) */
  promptStatus?: 'loading' | 'success' | 'error';
  /** 提示词验证错误信息 */
  promptErrorMessage?: string;
  /** 搜索结果状态: loading | success | empty (提示词有效但搜索结果为空) */
  searchStatus?: 'loading' | 'success' | 'empty';
  /** 搜索结果为空时的错误信息 */
  searchErrorMessage?: string;
  /** 错误回调 */
  onError?: (message: string) => void;
  className?: string;
}

export default function SearchAnimate({
  promptStatus = 'loading',
  promptErrorMessage,
  searchStatus = 'loading',
  searchErrorMessage,
  onError,
  className,
}: SearchAnimateProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [errorStep, setErrorStep] = useState<number | null>(null);
  const [dots, setDots] = useState('');
  const [progress, setProgress] = useState(0);
  const [isAnimationStopped, setIsAnimationStopped] = useState(false);
  const { t } = useTranslation();

  const thinkingSteps = useMemo(
    () => [
      { Icon: IconStepSearch, text: t('Understanding your search…'), rippleColor: '#725CFF' },
      { Icon: IconStepProfile, text: t('Finding the most relevant profiles…'), rippleColor: '#24A029' },
      { Icon: IconStepMatch, text: t('Evaluating the best matches…'), rippleColor: '#FFBF58' },
      { Icon: IconStepPrepare, text: t('Preparing personalized suggestions for you…'), rippleColor: '#717182' },
    ],
    [t]
  );

  // 计算进度 (3步，最大85%)
  const calculateProgress = useCallback((stepIndex: number, isCompleted: boolean) => {
    // 第0步: 0~28%, 第1步: 28~56%, 第2步: 56~85%
    const baseProgress = (stepIndex / 3) * 85;
    if (isCompleted) {
      return Math.min(baseProgress + 28, 85);
    }
    return Math.min(baseProgress + 14, 85);
  }, []);

  // 动画省略号 (错误时停止)
  useEffect(() => {
    if (isAnimationStopped) return;

    const dotsInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);
    return () => clearInterval(dotsInterval);
  }, [isAnimationStopped]);

  // 第1步：等待父组件 promptStatus
  useEffect(() => {
    if (currentStep === 0) {
      if (promptStatus === 'error' && promptErrorMessage) {
        setErrorStep(0);
        setIsAnimationStopped(true);
        onError?.(promptErrorMessage);
        return;
      }
      if (promptStatus === 'success') {
        setCompletedSteps((prev) => [...prev, 0]);
        setCurrentStep(1);
        setProgress(calculateProgress(1, false));
      }
    }
  }, [promptStatus, promptErrorMessage, currentStep, onError, calculateProgress]);

  // 第2步：随机5~10秒后进入第3步
  useEffect(() => {
    if (isAnimationStopped) return;
    if (currentStep !== 1) return;

    const randomDelay = Math.random() * 5000 + 5000; // 5~10秒

    const timer = setTimeout(() => {
      setCompletedSteps((prev) => [...prev, 1]);
      setCurrentStep(2);
      setProgress(calculateProgress(2, false));
    }, randomDelay);

    return () => clearTimeout(timer);
  }, [currentStep, isAnimationStopped, calculateProgress]);

  // 第3步：等待父组件 searchStatus
  useEffect(() => {
    if (currentStep !== 2) return;

    if (searchStatus === 'empty' && searchErrorMessage) {
      setErrorStep(2);
      setIsAnimationStopped(true);
      onError?.(searchErrorMessage);
    }
    // searchStatus === 'success' 时父组件会销毁该组件，无需处理
  }, [searchStatus, searchErrorMessage, onError, currentStep]);

  // 更新进度条
  useEffect(() => {
    setProgress(calculateProgress(currentStep, completedSteps.includes(currentStep)));
  }, [currentStep, completedSteps, calculateProgress]);

  return (
    <div className={cn('w-full', className)}>
      {/* 卡片容器 */}
      <div className="bg-white border border-[#D9D9D9] rounded-[24px] px-4 py-10 max-md:px-3 max-md:py-6">
        {/* 中心动画图标 */}
        <div className="flex justify-center mb-8 max-md:mb-6">
          <div className="relative inline-flex items-center justify-center">
            {/* AI炫彩光晕 - 水波涌动效果 */}
            {!isAnimationStopped && (
              <>
                <div className="absolute w-[90px] h-[90px] max-md:w-[70px] max-md:h-[70px] rounded-full ai-glow-wave-1" />
                <div className="absolute w-[90px] h-[90px] max-md:w-[70px] max-md:h-[70px] rounded-full ai-glow-wave-2" />
                <div className="absolute w-[90px] h-[90px] max-md:w-[70px] max-md:h-[70px] rounded-full ai-glow-wave-3" />
              </>
            )}
            <div className={cn('relative z-10', !isAnimationStopped && 'spin-slow')}>
              <IconThinkingBrain size={120} className="max-md:w-20 max-md:h-20" />
            </div>
          </div>
        </div>

        {/* 阶段列表 */}
        <div className="max-w-[672px] mx-auto space-y-4 max-md:space-y-3">
          {thinkingSteps.map((step, index) => {
            const { Icon } = step;
            const isActive = currentStep >= index;
            const isCurrent = currentStep === index && !isAnimationStopped;
            const isCompleted = completedSteps.includes(index);

            return (
              <div
                key={index}
                className={cn(
                  'flex items-center gap-4 px-4 py-4 rounded-[10px] transition-all duration-300',
                  'max-md:px-3 max-md:py-3 max-md:gap-3',
                  isActive ? 'bg-[#ECEEF2]/50 opacity-100 slide-in' : 'bg-[#ECEEF2]/20 opacity-30'
                )}
              >
                {/* 图标容器 */}
                <div className={cn('relative flex-shrink-0', isCurrent && 'animate-pulse')}>
                  {isCurrent && (
                    <div
                      className="absolute -inset-2 rounded-full animate-ping opacity-30"
                      style={{ backgroundColor: step.rippleColor }}
                    />
                  )}
                  <Icon size={40} className="max-md:w-8 max-md:h-8" />
                </div>

                {/* 文本 */}
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-base max-md:text-sm leading-6 tracking-[-0.31px] transition-colors',
                      isActive ? 'text-neutral-950' : 'text-[#717182]'
                    )}
                  >
                    {step.text}
                    {isCurrent && dots}
                  </p>
                </div>

                {/* 完成勾选 */}
                {isCompleted && (
                  <div className="w-5 h-5 bg-[#00C950] rounded-full flex items-center justify-center shrink-0 scale-in">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}

                {/* 错误图标 */}
                {errorStep === index && (
                  <div className="w-5 h-5 bg-[#F04438] rounded-full flex items-center justify-center shrink-0 scale-in">
                    <X className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 进度条 */}
        <div className="max-w-[672px] mx-auto mt-10 max-md:mt-6">
          <div className="relative h-2 bg-[#ECEEF2] rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#725CFF] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
            {!isAnimationStopped && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent shimmer" />
            )}
          </div>
        </div>

        {/* 提示文本 */}
        <div className="max-w-[672px] mx-auto mt-4">
          <p className="text-sm text-[#717182] text-center leading-5 tracking-[-0.15px]">
            {t('AI is reviewing your details and preparing the most relevant matches for you.')}
          </p>
        </div>
      </div>

      {/* 自定义动画 */}
      <style jsx>{`
        .spin-slow {
          animation: spin 3s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .ai-glow-wave-1 {
          background: radial-gradient(circle, #725cff 0%, transparent 70%);
          filter: blur(15px);
          opacity: 0.5;
          animation: wave1 4s ease-in-out infinite;
        }

        .ai-glow-wave-2 {
          background: radial-gradient(circle, #e93fff 0%, transparent 70%);
          filter: blur(18px);
          opacity: 0.4;
          animation: wave2 5s ease-in-out infinite;
        }

        .ai-glow-wave-3 {
          background: radial-gradient(circle, #00d4ff 0%, transparent 70%);
          filter: blur(20px);
          opacity: 0.4;
          animation: wave3 6s ease-in-out infinite;
        }

        @keyframes wave1 {
          0%,
          100% {
            transform: scale(1) translate(0, 0);
          }
          25% {
            transform: scale(1.1) translate(5px, -8px);
          }
          50% {
            transform: scale(0.95) translate(-6px, 5px);
          }
          75% {
            transform: scale(1.05) translate(8px, 6px);
          }
        }

        @keyframes wave2 {
          0%,
          100% {
            transform: scale(1) translate(0, 0);
          }
          33% {
            transform: scale(1.15) translate(-8px, 6px);
          }
          66% {
            transform: scale(0.9) translate(6px, -5px);
          }
        }

        @keyframes wave3 {
          0%,
          100% {
            transform: scale(1) translate(0, 0);
          }
          20% {
            transform: scale(1.08) translate(6px, 8px);
          }
          40% {
            transform: scale(0.92) translate(-5px, -6px);
          }
          60% {
            transform: scale(1.12) translate(-8px, 4px);
          }
          80% {
            transform: scale(0.96) translate(4px, -8px);
          }
        }

        .slide-in {
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .scale-in {
          animation: scaleIn 0.3s ease-out;
        }

        @keyframes scaleIn {
          from {
            transform: scale(0);
          }
          to {
            transform: scale(1);
          }
        }

        .shimmer {
          animation: shimmer 1.5s linear infinite;
        }

        @keyframes shimmer {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(200%);
          }
        }
      `}</style>
    </div>
  );
}
